// ui/share-viewer.ts — Cluster 21: read-only `#/s/<share-token>` viewer mode
// Moved from main.ts. All callbacks (syncReplay, show) are injected via
// set*Cb() so this module does not import main.ts.

declare const L: any;

import { $, qs } from './dom';
import { showToast } from './toast';
import { apiGetShareMeta, apiGetSharePings } from '../api/client';
import { ensureMap } from '../map/tracker';
import { getLeafletMap, getShareViewerPings, clearShareViewerPings, pushShareViewerPing } from '../state/app-state';
import { groupSessionsLocal, colorForToken } from '../state/cohort-model';
import { sessionColorKey, drawAccuracyCircle } from '../map/tracker';
import { replayClip, updateElevationPanel } from '../map/elevation';

// ── callbacks injected by main.ts boot() ──────────────────────────────────
// syncReplay is called from applyShareViewerPings; lives in main.ts.
let _syncReplayCb: (() => void) | null = null;
export function setShareViewerSyncReplayCb(fn: () => void): void { _syncReplayCb = fn; }
// show('create') is called from abortShareViewer; show() lives in main.ts / routing.
let _showCb: ((screen: string) => void) | null = null;
export function setShareViewerShowCb(fn: (screen: string) => void): void { _showCb = fn; }

/* ─────────────────────────────────────────────
   SHARE VIEWER (read-only `#/s/<share-token>` mode)
   ───────────────────────────────────────────── */
const SHARE_VIEWER_POLL_MS = 30_000;
// Clamp the share-trail fitBounds: a trail collapsed inside one quantization
// cell would otherwise zoom to street level on a pinpoint, reading as a
// frozen exact location. The accuracy-circle-extended bounds usually keep
// us well below this, but the clamp guards the degenerate single-cell case.
const SHARE_FIT_MAXZOOM = 16;
let shareViewerPollTimer: any = null;
let shareViewerToken: string | null = null;  // F1: current share token, for the re-show catch-up tick
let shareViewerLayer: any = null;     // L.LayerGroup holding the rendered trail
let shareViewerLastT: number | null = null;
export function getShareViewerPollTimer(){ return shareViewerPollTimer; }
export function getShareViewerToken(){ return shareViewerToken; }
// Session-aware: keep the raw pings (carrying session_id + precision_meters)
// rather than a flat coord list, so the viewer can colour per session and
// draw honest accuracy circles. Ascending by t (server orders that way).
// shareViewerPings moved to state/app-state (getShareViewerPings / setShareViewerPings).

export function parseShareHash(hash: string){
  // `#/s/<share-token>` (the token is URL-safe base64 of 18 bytes —
  // [A-Za-z0-9_-]+, length 24).
  const m = /^#\/s\/([A-Za-z0-9_-]{1,64})$/.exec(hash || '');
  return m ? m[1] : null;
}

export function fmtExpires(expires_at: number | null){
  if(expires_at === null || expires_at === undefined) return 'never expires';
  const ms = expires_at - Date.now();
  if(ms <= 0) return 'expired';
  const minutes = Math.round(ms / 60000);
  if(minutes < 60) return `expires in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if(hours < 48) return `expires in ${hours} h`;
  const days = Math.round(hours / 24);
  return `expires in ${days} d`;
}

const PRECISION_LABEL: Record<string, string> = { exact: 'exact precision', '1km': '~1 km precision', city: '~10 km precision' };
const RANGE_LABEL: Record<string, string>     = { '24h': 'last 24 h', '7d': 'last 7 days', all: 'all time' };

export function renderShareViewerHeader(scope: any){
  const el = $('share-viewer-header');
  if(!el) return;
  const parts = [
    RANGE_LABEL[scope.range] || scope.range,
    PRECISION_LABEL[scope.precision] || scope.precision,
    fmtExpires(scope.expires_at),
  ];
  el.textContent = `Shared view · ${parts.join(' · ')}`;
}

// Redraw the whole trail from `shareViewerPings`: one coloured polyline per
// session (via groupSessionsLocal, so server session_id boundaries drive
// the segmentation) plus an honest accuracy circle per session. Cheap to
// rebuild from scratch each call at V1 ping volumes (cap 10k, typically
// hundreds); avoids tracking per-session layer diffs.
export function renderShareViewerTrail(){
  if(!getLeafletMap()) return;
  updateElevationPanel();
  if(!shareViewerLayer) shareViewerLayer = L.layerGroup().addTo(getLeafletMap());
  shareViewerLayer.clearLayers();
  const sessions = groupSessionsLocal(replayClip(getShareViewerPings()));
  if(!sessions.length) return;
  sessions.forEach(s => {
    const color = colorForToken(sessionColorKey(s));
    L.polyline(s.coords, {
      color, weight: 3, opacity: 0.85, lineCap:'round', lineJoin:'round',
    }).addTo(shareViewerLayer);
    drawAccuracyCircle(shareViewerLayer, s.points[s.points.length - 1], color);
  });
  // Solid marker on the chronologically-latest point. groupSessionsLocal
  // returns newest-first, so sessions[0] holds the latest fix + its colour.
  const newest = sessions[0];
  const lastP = newest.points[newest.points.length - 1];
  L.circleMarker([lastP.lat, lastP.lng], {
    radius: 6, color: '#1F1B16', weight: 1.5,
    fillColor: colorForToken(sessionColorKey(newest)), fillOpacity: 1,
  }).addTo(shareViewerLayer);
}

export function startShareViewerPolling(shareToken: string){
  if(shareViewerPollTimer) return;    // idempotent
  shareViewerToken = shareToken;      // F1: remembered for the re-show catch-up tick
  shareViewerPollTimer = setInterval(() => shareViewerTick(shareToken), SHARE_VIEWER_POLL_MS);
}

export function stopShareViewerPolling(){
  if(shareViewerPollTimer){
    clearInterval(shareViewerPollTimer);
    shareViewerPollTimer = null;
  }
  shareViewerToken = null;
}

// Tear down the poll loop. Called on revoked/expired/not-found so a dead
// share stops costing requests.
export function stopShareViewer(){
  stopShareViewerPolling();
}

// Single render path for the poll response. Rows arrive newest-last
// (server orders by t ASC). The `shareViewerLastT` filter dedupes the
// window overlap each poll re-sends; the
// first non-empty batch fits the viewport to the shared trail (and, per
// the share-viewer guard in pickInitialMapView, never re-snaps to the
// viewer's own location).
export function applyShareViewerPings(rows: any[]){
  if(!getLeafletMap() || !Array.isArray(rows) || rows.length === 0) return;
  const fresh = rows.filter((p: any) => shareViewerLastT == null || p.t > shareViewerLastT);
  if(!fresh.length) return;
  const wasEmpty = getShareViewerPings().length === 0;
  for(const p of fresh) pushShareViewerPing(p);
  shareViewerLastT = fresh[fresh.length - 1].t;
  renderShareViewerTrail();
  if(wasEmpty){
    // First batch — frame the trail. Extend the bounds by the largest
    // accuracy circle so a trail collapsed inside one ~1km cell frames the
    // fuzzy area rather than a pinpoint; the maxZoom clamp backstops the
    // degenerate single-cell case. Never re-snaps to the viewer's own
    // location (pickInitialMapView early-returns in share-viewer mode).
    const svp = getShareViewerPings();
    let b = L.latLngBounds(svp.map((p: any) => [p.lat, p.lng]));
    const maxPrec = svp.reduce((m: number, p: any) => Math.max(m, p.precision_meters ?? 0), 0);
    if(maxPrec > 1){
      const last = svp[svp.length - 1];
      b = b.extend(L.latLng(last.lat, last.lng).toBounds(maxPrec * 2));
    }
    getLeafletMap().fitBounds(b.pad(0.15), { maxZoom: SHARE_FIT_MAXZOOM });
  }
  // Share-surface chokepoint: extend the scrubber range as deltas land
  // (follows the live edge only if the user is parked there).
  _syncReplayCb?.();
}

export async function shareViewerTick(shareToken: string){
  if(document.hidden) return;          // F1: skip hidden-tab ticks (re-show catches up)
  try {
    const res = await apiGetSharePings(shareToken, shareViewerLastT);
    if(!res){ stopShareViewer(); showToast('share not found'); return; }
    if('gone' in res){ stopShareViewer(); showToast('share has been revoked or expired'); return; }
    applyShareViewerPings(res.pings);
  } catch(err){
    console.warn('[locus] share viewer poll failed', err);
  }
}

export async function enterShareViewer(shareToken: string){
  // The screen the viewer was on before entering (null on a cold-boot share
  // link, which never ran boot()). Used to land them somewhere usable if the
  // share turns out to be dead.
  const priorScreen = qs('.screen.is-active')?.id || null;
  // Claim share-viewer mode synchronously (before any await) so a hashchange
  // racing the meta fetch sees the mode and reloads rather than re-entering;
  // a dead/unreachable share drops the claim again via abortShareViewer().
  document.body.dataset.mode = 'share-viewer';
  $('screen-map').classList.add('is-active');
  // The map only initialises on the map screen; we just forced that, so
  // ensureMap() is safe.
  ensureMap();

  // Fetch meta first so we only STAY in the chrome-hiding share-viewer mode
  // once the share is confirmed live — otherwise a bad/expired link would leave
  // a blank, chrome-locked screen with no way out.
  let meta: any;
  try {
    meta = await apiGetShareMeta(shareToken);
  } catch(err){
    console.warn('[locus] share viewer meta failed', err);
    showToast('share unreachable');
    abortShareViewer(priorScreen);
    return;
  }
  if(!meta){ showToast('share not found'); abortShareViewer(priorScreen); return; }
  if('gone' in meta){ showToast('share has been revoked or expired'); abortShareViewer(priorScreen); return; }
  renderShareViewerHeader({ range: meta.range, precision: meta.precision, expires_at: meta.expires_at });

  // Initial pings via a one-shot poll so the trail paints well under a
  // second. Pass since=null for the range floor.
  shareViewerLastT = null;
  clearShareViewerPings();
  if(shareViewerLayer) shareViewerLayer.clearLayers();
  await shareViewerTick(shareToken);

  // Keep the trail live with the 30 s poll; new pings land on each tick
  // (deduped by shareViewerLastT).
  startShareViewerPolling(shareToken);
}

// Back out of share-viewer mode after a dead/unreachable share, before we
// committed to the live trail: stop any poll, drop the chrome-hiding mode flag,
// and land the viewer somewhere usable — back on their own map if they opened
// the share from within the app, or the picker on a cold-boot share link.
export function abortShareViewer(priorScreen: string | null){
  stopShareViewer();
  delete document.body.dataset.mode;
  if(priorScreen === 'screen-map') return; // in-app opener: their map is still active
  $('screen-map').classList.remove('is-active');
  _showCb?.('create');
}
